import { Fragment, ReactNode, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { Tabs } from 'antd';
import cn from 'classnames';

import { parseQueryParams, stringifyQueryParams } from 'utils/query';

import styles from './MainTabs.module.scss';

export interface IMainTabsItem {
  key: string;
  title: ReactNode;
  content: ReactNode;
  isShow?: boolean;
}

interface IProps {
  items: Array<IMainTabsItem>;
  defaultActiveKey?: string;
  renderInactive?: boolean;
  queryParam?: string;
  className?: string;
  onChange?: (activeKey: string) => void;
}

const MainTabs = ({
  items,
  defaultActiveKey = items?.[0].key,
  renderInactive = true,
  queryParam,
  className = '',
}: IProps) => {
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const defaultKey = useMemo(() => {
    const queryParams = parseQueryParams(location.search);
    const keyFromQuery = String(queryParams[queryParam || '']);
    return items.some(({ key }) => key === keyFromQuery) ? keyFromQuery : defaultActiveKey;
  }, [defaultActiveKey, items, location.search, queryParam]);

  const [activeKey, setActiveKey] = useState(defaultKey);

  const handleTabChange = (activeTab: string) => {
    if (queryParam) {
      history.replace({
        pathname: match.url,
        search: stringifyQueryParams({ ...parseQueryParams(location.search), [queryParam]: activeTab }),
      });
    }
    setActiveKey(activeTab);
  };

  const renderItems = () => {
    return items.map(({ key, title, content, isShow = true }) => (
      <Fragment key={key}>
        {isShow && (
          <Tabs.TabPane tab={title} key={key}>
            {renderInactive || activeKey === key ? content : ''}
          </Tabs.TabPane>
        )}
      </Fragment>
    ));
  };

  return (
    <div className={cn(styles.tabs, className)}>
      <Tabs activeKey={activeKey} className={styles.tabs} onChange={handleTabChange}>
        {renderItems()}
      </Tabs>
    </div>
  );
};

export default MainTabs;
